/* ==========================================================================
   AJ FITS — Core runtime
   Scroll reveal, carousels, wishlist, toasts, quick view, recently viewed.
   No dependencies. Deferred; safe to load before or after DOM ready.
   ========================================================================== */
(function () {
  'use strict';

  var AJ = (window.AJ = window.AJ || {});
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Small helpers
     --------------------------------------------------------------------- */
  AJ.$ = function (sel, root) { return (root || document).querySelector(sel); };
  AJ.$$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  AJ.debounce = function (fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait || 150);
    };
  };

  AJ.throttle = function (fn, limit) {
    var waiting = false;
    return function () {
      if (waiting) return;
      fn.apply(this, arguments);
      waiting = true;
      setTimeout(function () { waiting = false; }, limit || 100);
    };
  };

  AJ.formatMoney = function (cents) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    // Indian digit grouping: last 3, then pairs (1,23,456)
    var parts = amount.split('.');
    var int = parts[0];
    var last3 = int.slice(-3);
    var rest = int.slice(0, -3);
    if (rest) last3 = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    return '₹' + last3 + (parts[1] ? '.' + parts[1] : '');
  };

  /* ---------------------------------------------------------------------
     Scroll reveal
     One shared IntersectionObserver for the whole page. Elements added
     later (AJAX filter results, infinite scroll) register via AJ.observe().
     --------------------------------------------------------------------- */
  var revealObserver = null;

  function initReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      AJ.$$('[data-aj-reveal], [data-aj-stagger]').forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          // Index stagger children so CSS can compute per-child delay
          if (el.hasAttribute('data-aj-stagger')) {
            Array.prototype.forEach.call(el.children, function (child, i) {
              child.style.setProperty('--aj-i', i);
            });
          }
          el.classList.add('is-revealed');
          revealObserver.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    AJ.observe(document);
  }

  AJ.observe = function (root) {
    var scope = root === document ? undefined : root;
    var els = AJ.$$('[data-aj-reveal], [data-aj-stagger]', scope);

    if (!revealObserver) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var fold = window.innerHeight || document.documentElement.clientHeight;

    els.forEach(function (el) {
      if (el.classList.contains('is-revealed') || el.dataset.ajArmed) return;
      el.dataset.ajArmed = '1';

      // Anything already on screen must paint immediately — arming it would
      // hold back Largest Contentful Paint until this script runs.
      var top = el.getBoundingClientRect().top;
      if (top < fold * 1.05) {
        el.classList.add('is-revealed');
        return;
      }

      el.classList.add('aj-armed');
      revealObserver.observe(el);
    });
  };

  /* ---------------------------------------------------------------------
     Hover images — fetched on first pointer entry, never on touch
     --------------------------------------------------------------------- */
  function initHoverImages(root) {
    // A coarse pointer never hovers, so the second image is pure waste there
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;

    AJ.$$('[data-aj-product-card]', root).forEach(function (card) {
      if (card.dataset.ajHoverReady) return;
      var img = AJ.$('img[data-aj-hover-src]', card);
      if (!img) return;
      card.dataset.ajHoverReady = '1';

      function load() {
        if (img.dataset.ajHoverSrcset) img.srcset = img.dataset.ajHoverSrcset;
        img.src = img.dataset.ajHoverSrc;
        img.removeAttribute('data-aj-hover-src');
        img.removeAttribute('data-aj-hover-srcset');
        card.removeEventListener('pointerenter', load);
        card.removeEventListener('focusin', load);
      }

      card.addEventListener('pointerenter', load, { once: true });
      card.addEventListener('focusin', load, { once: true });
    });
  }

  /* ---------------------------------------------------------------------
     Image load state — removes the placeholder tint once decoded
     --------------------------------------------------------------------- */
  function initImageStates(root) {
    AJ.$$('img[loading="lazy"], img[data-aj-fade]', root).forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('aj-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('aj-loaded'); }, { once: true });
        img.addEventListener('error', function () { img.classList.add('aj-loaded'); }, { once: true });
      }
    });
  }

  /* ---------------------------------------------------------------------
     Horizontal scrollers with prev/next buttons
     --------------------------------------------------------------------- */
  function initScrollers(root) {
    AJ.$$('[data-aj-scroller]', root).forEach(function (wrap) {
      if (wrap.dataset.ajScrollerReady) return;
      wrap.dataset.ajScrollerReady = '1';

      var track = AJ.$('[data-aj-scroller-track]', wrap);
      var prev = AJ.$('[data-aj-scroller-prev]', wrap);
      var next = AJ.$('[data-aj-scroller-next]', wrap);
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function sync() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;

        /* Only hide once the track has actually been measured. Before images
           lay out, scrollWidth equals clientWidth and this used to hide the
           nav permanently on rows that really do scroll. */
        var nav = prev.parentElement;
        if (!nav) return;
        if (track.clientWidth === 0) { nav.style.visibility = ''; return; }
        nav.style.visibility = max <= 2 ? 'hidden' : '';
      }

      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });

      track.addEventListener('scroll', AJ.throttle(sync, 100), { passive: true });
      window.addEventListener('resize', AJ.debounce(sync, 200));

      /* Re-measure once the images have laid out. A ResizeObserver on the track
         is not enough — loading an image changes scrollWidth but not the
         track's own box, so the observer never fires. */
      window.addEventListener('load', sync, { once: true });
      AJ.$$('img', track).forEach(function (img) {
        if (!img.complete) {
          img.addEventListener('load', sync, { once: true });
          img.addEventListener('error', sync, { once: true });
        }
      });
      // Belt and braces for lazy images that resolve after load
      setTimeout(sync, 600);
      setTimeout(sync, 1800);

      sync();
    });
  }

  /* ---------------------------------------------------------------------
     Toast notifications
     --------------------------------------------------------------------- */
  var toastHost = null;

  AJ.toast = function (message, opts) {
    opts = opts || {};
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'aj-toast-host';
      toastHost.setAttribute('role', 'status');
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }

    var el = document.createElement('div');
    el.className = 'aj-toast' + (opts.type ? ' aj-toast--' + opts.type : '');
    el.innerHTML =
      '<span class="aj-toast__icon">' + (opts.icon || checkIcon()) + '</span>' +
      '<span class="aj-toast__text"></span>' +
      (opts.action ? '<a class="aj-toast__action" href="' + opts.action.href + '">' + opts.action.label + '</a>' : '');
    AJ.$('.aj-toast__text', el).textContent = message;

    toastHost.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-in'); });

    setTimeout(function () {
      el.classList.remove('is-in');
      el.addEventListener('transitionend', function () { el.remove(); }, { once: true });
      setTimeout(function () { if (el.parentNode) el.remove(); }, 600);
    }, opts.duration || 3200);
  };

  function checkIcon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
  }

  /* ---------------------------------------------------------------------
     Wishlist — localStorage backed, no app required
     --------------------------------------------------------------------- */
  var WISHLIST_KEY = 'aj_wishlist_v1';

  AJ.wishlist = {
    read: function () {
      try {
        var raw = localStorage.getItem(WISHLIST_KEY);
        var list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },

    write: function (list) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, 200)));
      } catch (e) { /* quota or private mode — degrade silently */ }
      AJ.wishlist.syncBadge();
      document.dispatchEvent(new CustomEvent('aj:wishlist:change', { detail: { items: list } }));
    },

    has: function (handle) {
      return AJ.wishlist.read().some(function (i) { return i.handle === handle; });
    },

    toggle: function (item) {
      var list = AJ.wishlist.read();
      var idx = -1;
      for (var i = 0; i < list.length; i++) {
        if (list[i].handle === item.handle) { idx = i; break; }
      }
      var added;
      if (idx > -1) {
        list.splice(idx, 1);
        added = false;
      } else {
        list.unshift(item);
        added = true;
      }
      AJ.wishlist.write(list);
      return added;
    },

    remove: function (handle) {
      AJ.wishlist.write(AJ.wishlist.read().filter(function (i) { return i.handle !== handle; }));
    },

    syncBadge: function () {
      var count = AJ.wishlist.read().length;
      AJ.$$('[data-aj-wishlist-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    syncButtons: function (root) {
      var list = AJ.wishlist.read();
      AJ.$$('[data-aj-wishlist-toggle]', root).forEach(function (btn) {
        var on = list.some(function (i) { return i.handle === btn.dataset.handle; });
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var label = on ? 'Remove from wishlist' : 'Add to wishlist';
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
      });
    }
  };

  function initWishlist() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-aj-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var added = AJ.wishlist.toggle({
        handle: btn.dataset.handle,
        id: btn.dataset.productId,
        title: btn.dataset.title || '',
        url: btn.dataset.url || '',
        image: btn.dataset.image || '',
        price: btn.dataset.price || '',
        compare: btn.dataset.compare || '',
        available: btn.dataset.available !== 'false',
        addedAt: Date.now()
      });

      AJ.wishlist.syncButtons(document);
      btn.classList.add('is-pop');
      setTimeout(function () { btn.classList.remove('is-pop'); }, 400);

      AJ.toast(
        added ? 'Added to wishlist' : 'Removed from wishlist',
        added ? { action: { href: '/pages/wishlist', label: 'View' } } : {}
      );
    });

    AJ.wishlist.syncBadge();
    AJ.wishlist.syncButtons(document);
  }

  /* ---------------------------------------------------------------------
     Recently viewed products
     --------------------------------------------------------------------- */
  var RECENT_KEY = 'aj_recent_v1';

  AJ.recentlyViewed = {
    read: function () {
      try {
        var raw = localStorage.getItem(RECENT_KEY);
        var list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
      } catch (e) { return []; }
    },
    push: function (item) {
      if (!item || !item.handle) return;
      var list = AJ.recentlyViewed.read().filter(function (i) { return i.handle !== item.handle; });
      list.unshift(item);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 20))); } catch (e) {}
    }
  };

  /* ---------------------------------------------------------------------
     Cart — add to cart with optimistic UI + drawer/notification refresh
     --------------------------------------------------------------------- */
  AJ.cart = {
    add: function (payload) {
      return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart');
          return data;
        });
      });
    },

    get: function () {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
    },

    refreshBadge: function () {
      return AJ.cart.get().then(function (cart) {
        AJ.$$('[data-aj-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        document.dispatchEvent(new CustomEvent('aj:cart:change', { detail: { cart: cart } }));
        return cart;
      });
    }
  };

  /* ---------------------------------------------------------------------
     Accordions — <details>-free so we can animate height
     --------------------------------------------------------------------- */
  function initAccordions(root) {
    AJ.$$('[data-aj-accordion]', root).forEach(function (acc) {
      if (acc.dataset.ajAccReady) return;
      acc.dataset.ajAccReady = '1';
      var single = acc.dataset.ajAccordion === 'single';

      AJ.$$('[data-aj-accordion-trigger]', acc).forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var item = trigger.closest('[data-aj-accordion-item]');
          var panel = AJ.$('[data-aj-accordion-panel]', item);
          var open = item.classList.contains('is-open');

          if (single && !open) {
            AJ.$$('[data-aj-accordion-item].is-open', acc).forEach(function (other) {
              closePanel(other);
            });
          }
          open ? closePanel(item) : openPanel(item, panel, trigger);
        });
      });

      // Open any item marked as default-open
      AJ.$$('[data-aj-accordion-item][data-open="true"]', acc).forEach(function (item) {
        openPanel(item, AJ.$('[data-aj-accordion-panel]', item), AJ.$('[data-aj-accordion-trigger]', item));
      });
    });
  }

  function openPanel(item, panel, trigger) {
    item.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (!panel) return;
    panel.hidden = false;
    panel.style.height = 'auto';
    var target = panel.scrollHeight;
    panel.style.height = '0px';
    requestAnimationFrame(function () {
      panel.style.height = target + 'px';
    });
    panel.addEventListener('transitionend', function done(e) {
      if (e.propertyName !== 'height') return;
      panel.style.height = 'auto';
      panel.removeEventListener('transitionend', done);
    });
  }

  function closePanel(item) {
    var panel = AJ.$('[data-aj-accordion-panel]', item);
    var trigger = AJ.$('[data-aj-accordion-trigger]', item);
    item.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (!panel) return;
    panel.style.height = panel.scrollHeight + 'px';
    requestAnimationFrame(function () { panel.style.height = '0px'; });
  }

  /* ---------------------------------------------------------------------
     Modal / dialog
     --------------------------------------------------------------------- */
  var lastFocused = null;

  AJ.openModal = function (id) {
    var modal = document.getElementById(id);
    if (!modal) return;
    lastFocused = document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { modal.classList.add('is-open'); });

    var focusable = AJ.$$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', modal);
    if (focusable.length) focusable[0].focus();
    modal.addEventListener('keydown', trapFocus);
  };

  AJ.closeModal = function (modal) {
    if (typeof modal === 'string') modal = document.getElementById(modal);
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.removeEventListener('keydown', trapFocus);
    document.body.style.overflow = '';
    setTimeout(function () { modal.hidden = true; }, 320);
    if (lastFocused) lastFocused.focus();
  };

  function trapFocus(e) {
    if (e.key === 'Escape') return AJ.closeModal(e.currentTarget);
    if (e.key !== 'Tab') return;
    var f = AJ.$$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', e.currentTarget)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function initModals(root) {
    AJ.$$('[data-aj-modal-open]', root).forEach(function (btn) {
      if (btn.dataset.ajModalReady) return;
      btn.dataset.ajModalReady = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        AJ.openModal(btn.dataset.ajModalOpen);
      });
    });
    AJ.$$('[data-aj-modal]', root).forEach(function (modal) {
      if (modal.dataset.ajModalReady) return;
      modal.dataset.ajModalReady = '1';
      modal.addEventListener('click', function (e) {
        // closest(), not matches() — clicks usually land on the icon inside
        // the button, which would never match the selector directly.
        if (e.target.closest('[data-aj-modal-close]') || e.target.classList.contains('aj-modal__overlay')) {
          AJ.closeModal(modal);
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
     Back to top
     --------------------------------------------------------------------- */
  function initBackToTop() {
    var btn = AJ.$('[data-aj-back-to-top]');
    if (!btn) return;
    var onScroll = AJ.throttle(function () {
      btn.classList.toggle('is-visible', window.scrollY > 700);
    }, 150);
    window.addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    onScroll();
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot() {
    document.documentElement.classList.add('aj-clip');
    initReveal();
    initImageStates(document);
    initScrollers(document);
    initWishlist();
    initAccordions(document);
    initHoverImages(document);
    initModals(document);
    initBackToTop();
  }

  AJ.refresh = function (root) {
    root = root || document;
    AJ.observe(root);
    initImageStates(root);
    initScrollers(root);
    initAccordions(root);
    initHoverImages(root);
    initModals(root);
    AJ.wishlist.syncButtons(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor support — re-init when a section is re-rendered
  document.addEventListener('shopify:section:load', function (e) { AJ.refresh(e.target); });
  document.addEventListener('shopify:section:select', function (e) { AJ.refresh(e.target); });
})();
